import React, { useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { useActiveWeb3React } from '../../hooks'

import { ButtonPrimary } from '../../components/Button'
import { useWalletModalToggle } from '../../state/application/hooks'

import IntroCard from 'components/IntroCard'
import { TYPE } from 'theme'
import axios from 'axios'
import { AutoColumn } from 'components/Column'
import { Text } from 'rebass'
import ReferralInputPanel from '../PointsProgram/ReferralInputPanel'
import { API_POOL_BASE_URL } from 'constants/links'
import { Helmet } from 'react-helmet'

export const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 14px;
  display: flex;
  gap: 2px;
`

export const StyledTabButton = styled.button<{ isActive?: boolean }>`
  ${({ theme }) => theme.flexRowNoWrap}
  border: none;
  align-items: center;
  justify-content: center;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: white;
  padding: 8px 16px;
  border-radius: 14px;
  font-weight: 400;
  font-size: 14px;
  transition: all 300ms;
  background: ${({ isActive }) => (isActive ? '#4AFC38' : 'transparent')};
  color: ${({ isActive }) => (isActive ? 'black' : 'white')};

  &:hover {
    color: #4afc38;
  }

  ${({ isActive }) =>
    isActive &&
    `
      &:hover {
        color: black !important;
      }
    `}
`

const ContainerStyled = styled.div`
  width: 100%;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 200px;
  @media (min-width: 502px) {
    padding: 10px;
  }

  max-width: 500px;
`

const SubContainer = styled.div<{ bg1: boolean }>`
  background: ${({ theme }) => theme.bg0};
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

const SubContainerBody = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.bg0};
`

const ConnectWalletContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  min-height: 25vh;
  background: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  width: 100%;

  div {
    max-width: 300px;
  }
`

const SignReferral = () => {
  const { account, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const theme = useContext(ThemeContext)
  const [loading, setLoading] = useState(false)

  const [inputValue, setInputValue] = useState<string>('')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [successMsg, setSuccessMsg] = useState<string>('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const alphanumericRegex = /^[a-zA-Z0-9]*$/

    if (value.length <= 18 && alphanumericRegex.test(value)) {
      setInputValue(value)
    }
  }

  const sign = async () => {
    if (!library || !account) return

    setLoading(true)
    const signer = library.getSigner()
    const timestamp = Math.floor(Date.now() / 1000)
    const message = `Please sign this message to confirm that you are the owner. Timestamp: ${timestamp}`
    setErrorMsg('')
    setSuccessMsg('')
    try {
      const signedMessage = await signer.signMessage(message)

      const response = await axios.post(`${API_POOL_BASE_URL}/api/signature`, {
        address: account,
        id: inputValue,
        signature: signedMessage,
        message: message,
      })

      if (response.data?.message?.length > 0) {
        setSuccessMsg(response.data?.message)
      }

      if (!response.data.success) {
        throw new Error('Failed to submit signature')
      }
    } catch (error: any) {
      setSuccessMsg('')
      setErrorMsg(error?.response?.data?.message ? error?.response?.data?.message : 'An error occurred.')
      console.error('Error signing or sending message:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Register Points Program | HyperSwap Exchange on Hyperliquid</title>
        <meta name="description" content="" />
        <meta
          name="keywords"
          content="HyperSwap, test platform, faucet, points program, DeFi testing, Hyperliquid blockchain"
        />
        <meta property="og:title" content="Points Program | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Welcome to the Points Program! Experiment with HyperSwap's features, and explore our points program."
        />
        <meta property="og:url" content={`${window.location.origin}/#/testnet`} />
      </Helmet>
      <ContainerStyled>
        <IntroCard variant={1} key="Testnet">
          <h1>Register</h1>
          <p>This page is for signing up to the point program.</p>
        </IntroCard>

        <>
          <SubContainer bg1={false}>
            <SubContainerBody>
              {account ? (
                <AutoColumn gap={'md'}>
                  <ReferralInputPanel
                    id="referral-input"
                    value={inputValue}
                    handleChange={handleChange}
                    error={!!errorMsg}
                    disabled={false}
                  />

                  {errorMsg && <p style={{ color: '#FF6871', margin: 0, fontSize: '12px' }}>Error: {errorMsg}</p>}
                  {successMsg && successMsg.length > 0 && (
                    <p style={{ color: '#4AFB39', margin: 0, fontSize: '12px' }}>Success: {successMsg}</p>
                  )}

                  <ButtonPrimary
                    id="join-pool-button"
                    style={{ padding: 16 }}
                    as={'button'}
                    onClick={sign}
                    disabled={loading || inputValue.length === 0}
                  >
                    <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {loading ? 'Signing...' : 'Sign'}
                    </Text>
                  </ButtonPrimary>
                </AutoColumn>
              ) : (
                <ConnectWalletContainer>
                  <div>
                    <TYPE.mediumHeader textAlign="center" color={theme.text3}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-user"
                        style={{ marginBottom: '.5rem' }}
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                        <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                      </svg>

                      <div>{'Connect your wallet to register.'}</div>
                    </TYPE.mediumHeader>
                    <ButtonPrimary style={{ marginTop: '2em', padding: '8px 16px' }} onClick={toggleWalletModal}>
                      Connect a wallet
                    </ButtonPrimary>
                  </div>
                </ConnectWalletContainer>
              )}
            </SubContainerBody>
          </SubContainer>
        </>
      </ContainerStyled>
    </>
  )
}
export default SignReferral
